import React from "react";

import { admin_cse, admin_csm } from "@/config/roles";
import { Button2 } from "@/ui2/atoms/Button";
import { Checkbox2 } from "@/ui2/atoms/Checkbox";
import { cn } from "@/lib/helpers/cn";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { getRole, isAuthorized } from "@/utils/authToken";
import images from "@/utils/images";
import InfoIcon from "@/assets/images/icons2/icon--information.svg?component";
import type { Note } from "@/core/types";
import { NoteBadges } from "@/features/notes/(v2)/components/NoteBadges";
import { NoteMetaData } from "@/features/notes/(v2)/components/NoteMetaData";
import { testLocator } from "@/lib/helpers/test-locator";
import { Tooltip2 } from "@/components/UI2/molecules/Tooltip";

import { getNoteIcon } from "../helpers/get-note-icon";
import { isNoteSelectable } from "../helpers/isNoteSelectable";

import { NoteTitleBlock } from "./NoteTitleBlock";

type NoteItemProps = {
  isSelectedForExport: boolean;
  note: Note;
  onToggleSelection: () => void;
  onView: () => void;
};

export function NoteListItem({
  isSelectedForExport,
  note,
  onToggleSelection,
  onView,
}: NoteItemProps) {
  const isAuthorizedToExportNotes = isAuthorized(admin_cse) || isAuthorized(admin_csm);
  const icon = getNoteIcon(note);
  const isDraft = note.note_state === "draft";
  const isRejected = note.note_state === "rejected";
  const isDimmed = isDraft || isRejected;
  const isExportDisabled = !isNoteSelectable(note);

  return (
    <li key={note.id} {...getAutomationDataAttr("member-profile-exportable-note")}>
      <DashboardCard2
        className="tw:p-0!"
        classNameChildren={cn(
          "tw:grid tw:gap-4 tw:max-lg:justify-items-start tw:lg:items-center",
          "tw:min-h-24 tw:px-7 tw:py-3",
          isAuthorizedToExportNotes
            ? "tw:lg:grid-cols-[min-content_36px_2fr_0.6fr_0.75fr_min-content] tw:lg:[grid-template-areas:'checkbox_icon_title_badges_meta_action']"
            : "tw:lg:grid-cols-[36px_2fr_0.6fr_0.75fr_min-content] tw:lg:[grid-template-areas:'icon_title_badges_meta_action']",
        )}
      >
        {isAuthorizedToExportNotes && (
          <div className="tw:mr-2 tw:lg:[grid-area:checkbox]">
            {isExportDisabled ? (
              <Tooltip2
                className="tw:max-w-66"
                icon={<InfoIcon />}
                text="You can only export cosigned and non-draft notes."
              >
                <Checkbox2 isDisabled isSelected={false} />
              </Tooltip2>
            ) : (
              <Checkbox2 isSelected={isSelectedForExport} onClick={onToggleSelection} />
            )}
          </div>
        )}

        <img
          alt=""
          className={cn("tw:h-9 tw:lg:[grid-area:icon]", isDimmed && "tw:opacity-50")}
          src={images(`./icons/${icon}`)}
        />

        <NoteTitleBlock className="tw:lg:[grid-area:title]" note={note} />
        <NoteBadges badgeSize="xs" className="tw:lg:[grid-area:badges]" note={note} />
        <NoteMetaData className="tw:lg:[grid-area:meta]" note={note} />

        {isAuthorizedToViewFullNote(note) && (
          <Button2
            className="tw:p-1 tw:lg:[grid-area:action]"
            onPress={onView}
            variant="link"
            {...getAutomationDataAttr("member-profile-view-full-note-button")}
            {...getAutomationDataAttr(note?.id, "data-id")}
            {...testLocator(`viewFullNoteBtn-${note.id}`)}
          >
            View Note
          </Button2>
        )}
      </DashboardCard2>
    </li>
  );
}

const isAuthorizedToViewFullNote = (note: Note): boolean => {
  const isBhsTherapySessionNote =
    (note.provider_type === "BEHAVIORAL" || note.provider_type === "BEHAVIORAL_INTAKE") &&
    (note.note_type === "Video Consultation" || note.version === 2);

  switch (getRole()) {
    case "provider:pcp":
      return !isBhsTherapySessionNote;

    default:
      return true;
  }
};
